<template>
  <nav
    :class="b({ disabled })"
    :aria-label="$t('c-pagination.pagination-description')"
  >
    <div :class="b('inner', { hasAdvancedControls: showAdvancedControls })">
      <div
        v-if="showAdvancedControls"
        :class="b('items-count-label')"
      >
        {{ itemsCountLabel }}
      </div>
      <ul :class="b('list')">
        <li :class="b('item')">
          <button
            :class="b('trigger', { previous: true })"
            :disabled="disabled || pagination.current === 1"
            :aria-label="$t('c-pagination.button-previous-page')"
            type="button"
            @click="onClickPage(pagination.current - 1)"
          >
            <e-icon
              icon="i-chevron--left"
              size="15"
            />
          </button>
        </li>
        <li
          v-for="page in pageTriggers"
          :key="page.number"
          :class="b('item', { pageButton: true, ...page.modifiers })"
        >
          <button
            :class="b('trigger')"
            :aria-label="$t('c-pagination.label-page', { page: page.number })"
            :aria-current="pagination.current === page.number"
            :disabled="disabled"
            type="button"
            @click="onClickPage(page.number)"
          >
            {{ page.number }}
          </button>
        </li>
        <li :class="b('item')">
          <button
            :class="b('trigger', { next: true })"
            :disabled="disabled || pagination.current >= pagination.pageCount"
            :aria-label="$t('c-pagination.button-next-page')"
            type="button"
            @click="onClickPage(pagination.current + 1)"
          >
            <e-icon
              icon="i-chevron--right"
              size="15"
            />
          </button>
        </li>
      </ul>
      <button
        v-if="showAdvancedControls"
        type="button"
        :class="b('back-to-top')"
        @click="onClickBackToTop"
      >
        <span :class="b('back-to-top-label')">{{ $t('c-pagination.back-to-top') }}</span>
        <span :class="b('back-to-top-icon-wrapper')">
          <e-icon
            :class="b('back-to-top-icon')"
            icon="i-chevron--up"
            size="16"
            inline
          />
        </span>
      </button>
    </div>
  </nav>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue';
  import eIcon from '@/elements/e-icon.vue';
  import { Pagination } from '@/helpers/pagination';

  export type PageTrigger = {
    number: number;
    modifiers: {
      [key: string]: boolean;
    };
  };

  // type Setup = {};
  // type Data = {};

  /**
   * Renders a list pagination.
   */
  export default defineComponent({
    name: 'c-pagination',

    components: {
      eIcon,
    },

    props: {
      /**
       * Expects a pagination object.
       */
      pagination: {
        type: Object as PropType<Pagination>,
        required: true,
      },

      /**
       * The maximum of page numbers to show.
       * Has to be an uneven number. Includes first and last page that will always be displayed.
       */
      maxAmountOfPages: {
        type: Number,
        default: 7,
      },

      /**
       * Allows to disable the pagination controls.
       */
      disabled: {
        type: Boolean,
        default: false,
      },

      /**
       * if the advanced controls are show
       * - items items count
       * - back to top button
       */
      showAdvancedControls: {
        type: Boolean,
        default: false,
      },
    },
    emits: {
      change: (() => true) as (page: number) => void,
    },

    // setup(): Setup {},
    // data(): Data {
    //   return {
    //   };
    // },

    computed: {
      /**
       * Returns the list of page triggers.
       * If there are to many pages, we show '...'-indicators
       * for the user to know that there are more pages before or after.
       */
      pageTriggers(): PageTrigger[] {
        const { current, pageCount } = this.pagination;
        const pagesAroundCurrentPage = (this.maxAmountOfPages - 3) / 2;

        return new Array(pageCount).fill(null).map((item, index) => {
          const number = index + 1;
          let isVisible = false;
          let morePagesAfterIndicator = false;
          let morePagesBeforeIndicator = false;

          if (pageCount <= this.maxAmountOfPages || number === 1 || number === pageCount) {
            isVisible = true;
          } else if (number >= current - pagesAroundCurrentPage && number <= current + pagesAroundCurrentPage) {
            isVisible = true;

            if (number === current - pagesAroundCurrentPage && number > 2) {
              morePagesBeforeIndicator = true;
            }

            if (number === current + pagesAroundCurrentPage && number < pageCount - 1) {
              morePagesAfterIndicator = true;
            }
          }

          return {
            number,
            modifiers: {
              isVisible,
              morePagesAfterIndicator,
              morePagesBeforeIndicator,
            },
          };
        });
      },

      itemsCountLabel(): string {
        return `${this.pagination.numItemsPerPage} - ${this.pagination.totalCount}`;
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Handles click on a page.
       */
      onClickPage(page: number): void {
        this.$emit('change', page);

        // Focus the new current page button
        this.$nextTick(() => {
          const button = this.$el.querySelector(`[aria-current='page']`);

          if (button) {
            button.focus();
          }
        });
      },

      /**
       * Handles click on back to top button.
       */
      onClickBackToTop(): void {
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';

  .c-pagination {
    $size: 2.5rem;

    display: flex;
    justify-content: center;

    &__inner {
      width: 100%;
      display: flex;
      align-items: center;
      flex-direction: column;
      gap: 2rem;

      @include mixins.media(md) {
        flex-direction: row;
        justify-content: center;
        gap: 1rem;

        &--has-advanced-controls {
          justify-content: space-between;
        }
      }
    }

    &__items-count-label {
      color: var(--theme-color-grayscale--400);
    }

    &__list {
      display: flex;
    }

    &__back-to-top {
      cursor: pointer;
      display: flex;
      flex-flow: row nowrap;
      gap: 0.5rem;
      align-items: center;
      color: var(--theme-color-grayscale--400);

      &:hover {
        @include mixins.hover-effect--default();
      }
    }

    &__back-to-top-icon-wrapper {
      width: 2rem;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid var(--theme-color-grayscale--400);
      border-radius: 50%;
    }

    &__item {
      user-select: none;

      &--page-button {
        display: none;
      }

      &--is-visible {
        display: flex;
        align-items: baseline;
      }

      &::before,
      &::after {
        color: var(--theme-color-primary--1);
        padding: 0 1rem;
      }

      &--more-pages-before-indicator::before {
        content: '...';
      }

      &--more-pages-after-indicator::after {
        content: '...';
      }
    }

    &__trigger {
      display: flex;
      justify-content: center;
      align-items: center;
      height: $size;
      width: $size;
      color: var(--color-scheme--secondary);
      border-radius: 100%;

      &--previous,
      &--next {
        background-color: var(--theme-color-primary--1);
        color: var(--theme-color-grayscale--1000);
      }

      &--previous {
        margin-right: 1rem;
      }

      &--next {
        margin-left: 1rem;
      }

      &[aria-current='true'] {
        border: 1px solid var(--theme-color-primary--1);
        pointer-events: none;
      }

      &:hover {
        @include mixins.hover-effect--default();

        text-decoration: underline;
      }

      &[disabled],
      &[disabled]:hover {
        cursor: default;
        opacity: 0.6;
      }
    }

    &--disabled {
      .c-pagination__item {
        &::before,
        &::after {
          color: var(--theme-color-primary--1);
        }
      }
    }
  }
</style>
